import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { debounce } from 'lodash';
import Box from '@bit/knotchinc.web-react-components.box';
import Dropdown from 'components/Global/Dropdown/Dropdown.jsx';
import RoundedModal from 'components/Global/RoundedModal/RoundedModal.jsx';
import Mixpanel from 'helpers/Mixpanel.js';
import SearchEntityInput from './SearchEntityInput.jsx';
import SearchKeywordInput from './SearchKeywordInput.jsx';
import SearchTextInput from './SearchTextInput.jsx';
import SearchDateInput from './SearchDateInput.jsx';
import {
  fetchAllKeywords,
  fetchAllBrands,
  fetchAllPublishers,
  SET_ADVANCED_SEARCH_KEYWORDS_FILTER,
  SET_ADVANCED_SEARCH_BRANDS_FILTER,
  SET_ADVANCED_SEARCH_PUBLISHERS_FILTER,
  RESET_ADVANCED_SEARCH_BRANDS_FILTER,
  RESET_ADVANCED_SEARCH_PUBLISHERS_FILTER,
  RESET_ADVANCED_SEARCH_KEYWORDS_FILTER,
  UPDATE_ADVANCED_SEARCH,
  UPDATE_ADVANCED_SEARCH_ENTITY,
  UPDATE_ADVANCED_SEARCH_DATES,
  submitAdvancedSearch,
} from './actions';
import { StyledPowerSearchContainer, SubmitButton, CenterContainer } from './styled.js';

const filterActionMap = {
  publishers: SET_ADVANCED_SEARCH_PUBLISHERS_FILTER,
  brands: SET_ADVANCED_SEARCH_BRANDS_FILTER,
  keyword_tags: SET_ADVANCED_SEARCH_KEYWORDS_FILTER,
};

class AdvancedSearchView extends React.Component {
  constructor(props) {
    super(props);
    this.search = debounce((section, value, type) => {
      const filters = {
        searchQuery: value,
        searchBy: value ? type : '' };
      this.props.setListFilter(section, filters);
    }, 100);
  }

  componentDidMount() {
    this.props.getKeywords(true);
    this.props.getBrands(true);
    this.props.getPublishers(true);
    Mixpanel.track('view Advanced Search');
  }

  submit() {
    Mixpanel.track('submitted advanced search');
    this.props.close();
    this.props.submit();
    this.props.history.push('/blueprint/search');
  }

  render() {
    const {
      keywordsList,
      brandsList,
      publishersList,
      selectedBrands,
      selectedPublishers,
      selectedKeywords,
      contentType,
      contentFormat,
      articleLength,
      searchQuery,
      updateAdvancedSearch,
      updateAdvancedSearchDates,
      updateAdvancedSearchEntity,
      getBrands,
      getKeywords,
      getPublishers,
      resetBrandsSearch,
      resetPublishersSearch,
      resetKeywordsSearch,
      isOpen,
      close,
     } = this.props;

    return (
      <RoundedModal close={close} isOpen={isOpen} title="Advanced Search">
        <StyledPowerSearchContainer>
          <CenterContainer>
            <Box padding="60px">
              <Box verticalAlign="top" display="inline-block" width="35%" pr="60px">
                <Box mb="20px">
                  <SearchDateInput label="Dates" onUpdate={dates => updateAdvancedSearchDates(dates)} themed />
                </Box>
                <Box mb="20px">
                  <Dropdown
                    label="Content"
                    defaultOption="Paid & Owned"
                    options={[
                  { text: 'Paid', value: 'paid' },
                  { text: 'Owned', value: 'owned' },
                    ]}
                    value={contentType}
                    onClick={value => updateAdvancedSearch('content_type', value)}
                  />
                </Box>
                <Box mb="20px">
                  <Dropdown
                    label="Format"
                    defaultOption="All"
                    options={[
                  { text: 'Article', value: 'article' },
                  { text: 'Video', value: 'video' },
                    ]}
                    value={contentFormat}
                    onClick={value => updateAdvancedSearch('content_format', value)}
                  />
                </Box>
                <Box mb="20px">
                  <Dropdown
                    label="Length"
                    defaultOption="All"
                    options={[
                  { text: '< 300 Words', value: 'short' },
                  { text: '> 300 Words', value: 'medium' },
                  { text: '> 1000 Words', value: 'long' },
                    ]}
                    value={articleLength}
                    onClick={value => updateAdvancedSearch('article_length', value)}
                  />
                </Box>
              </Box>
              <Box display="inline-block" width="60%">
                <SearchEntityInput
                  label="Brands"
                  entitiesList={brandsList}
                  selectedIds={selectedBrands}
                  onUpdate={value => updateAdvancedSearchEntity(value, 'brand')}
                  search={value => this.search('brands', value, 'name')}
                  fetch={clear => getBrands(clear)}
                  filterAction={filterActionMap.brands}
                  reset={resetBrandsSearch}
                />
                <SearchEntityInput
                  label="Publishers"
                  entitiesList={publishersList}
                  selectedIds={selectedPublishers}
                  onUpdate={value => {
                    updateAdvancedSearchEntity(value, 'publisher');
                    updateAdvancedSearch('content_type', 'paid');
                  }}
                  search={value => this.search('publishers', value, 'name')}
                  fetch={clear => getPublishers(clear)}
                  disabled={contentType === 'owned'}
                  filterAction={filterActionMap.publishers}
                  reset={resetPublishersSearch}
                />
                <SearchKeywordInput
                  label="Keywords"
                  keywordsList={keywordsList}
                  selectedKeywords={selectedKeywords}
                  onUpdate={value => updateAdvancedSearch('keyword_tags', value)}
                  search={value => this.search('keyword_tags', value, 'value')}
                  fetch={clear => getKeywords(clear)}
                  filterAction={filterActionMap.keyword_tags}
                  reset={resetKeywordsSearch}
                />
                <SearchTextInput
                  label="Titles"
                  selected={searchQuery}
                  onUpdate={value => {
                    updateAdvancedSearch('searchBy', value ? 'title' : '');
                    updateAdvancedSearch('searchQuery', value);
                  }}
                />
              </Box>
              <SubmitButton onClick={() => this.submit()}>
                SUBMIT
              </SubmitButton>
            </Box>
          </CenterContainer>
        </StyledPowerSearchContainer>
      </RoundedModal>
    );
  }
}

const mapStateToProps = ({
  AdvancedSearch: {
    keywordsList,
    brandsList,
    publishersList,
    brand_ids,
    publisher_ids,
    keyword_tags,
    content_type,
    content_format,
    article_length,
    searchQuery } }) => ({
      keywordsList,
      brandsList,
      publishersList,
      selectedBrands: brand_ids,
      selectedPublishers: publisher_ids,
      selectedKeywords: keyword_tags,
      contentType: content_type,
      contentFormat: content_format,
      articleLength: article_length,
      searchQuery,
    });

const mapDispatchToProps = dispatch => ({
  setListFilter: (section, filters) => dispatch({ type: filterActionMap[section], filters }),
  getKeywords: clear => dispatch(fetchAllKeywords(clear)),
  getBrands: clear => dispatch(fetchAllBrands(clear)),
  getPublishers: clear => dispatch(fetchAllPublishers(clear)),
  resetBrandsSearch: () => dispatch({ type: RESET_ADVANCED_SEARCH_BRANDS_FILTER }),
  resetPublishersSearch: () => dispatch({ type: RESET_ADVANCED_SEARCH_PUBLISHERS_FILTER }),
  resetKeywordsSearch: () => dispatch({ type: RESET_ADVANCED_SEARCH_KEYWORDS_FILTER }),
  updateAdvancedSearch: (field, value) => dispatch({ type: UPDATE_ADVANCED_SEARCH, data: { field, value } }),
  updateAdvancedSearchEntity: (value, entityType) => dispatch({ type: UPDATE_ADVANCED_SEARCH_ENTITY, data: { value, entityType } }),
  updateAdvancedSearchDates: dates => dispatch({ type: UPDATE_ADVANCED_SEARCH_DATES, data: dates }),
  submit: () => dispatch(submitAdvancedSearch()),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AdvancedSearchView));
